import {Box, Button, Typography} from '@viptech/react-components'
import {isSameDay} from 'date-fns'
import startOfDay from 'date-fns/startOfDay'
import sub from 'date-fns/sub'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import CalendarIcon from 'src/images/CalendarIcon'
import ClockIconCalendar from 'src/images/ClockIconCalendar'
import {useWindowSize} from 'usehooks-ts'
import Calendar from '../calendar/Calendar'
import {Input} from '../input/Input'
import Menu from '../menu/Menu'
import Separation from '../separation/Separation'
import {CalendarBox, CalendarButton, ContainerInsideButton, DateBox, InputBox, ModalBox, ResponsiveBox, RulerButton} from './CustomCalendarBase'

type CustomCalendarProps = {
  setDate: (dates: [Date, Date]) => void
  sizeToHideRuler?: number
  hideRuler?: boolean
  hideTime?: boolean
  initialDates?: [Date, Date]
  setLabelTimeSelected?: (labelSelected: string) => void
  disableRangeOnCalendar?: boolean
  hideLabel?: boolean
}

function CustomCalendar({
  setDate,
  initialDates,
  sizeToHideRuler,
  hideRuler: isRulerHidden,
  hideTime,
  setLabelTimeSelected,
  disableRangeOnCalendar,
  hideLabel = false,
}: CustomCalendarProps) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const today = new Date()
  const tomorrow = new Date().setDate(today.getDate() + 1)
  const dateTomorrow = startOfDay(new Date(tomorrow))
  const hideRulerOnSize = sizeToHideRuler ?? 1420
  const valuesOnRuler = [
    {onClick: () => setDate([sub(new Date(), {minutes: 15}), new Date()]), label: '15min', responsiveLabel: 'Últimos 15 minutos'},
    {onClick: () => setDate([sub(new Date(), {minutes: 30}), new Date()]), label: '30min', responsiveLabel: 'Últimos 30 minutos'},
    {onClick: () => setDate([sub(new Date(), {hours: 1}), new Date()]), label: '1h', responsiveLabel: 'Última 1 hora'},
    {onClick: () => setDate([sub(new Date(), {hours: 6}), new Date()]), label: '6h', responsiveLabel: 'Últimas 6 horas'},
    {onClick: () => setDate([sub(new Date(), {hours: 12}), new Date()]), label: '12h', responsiveLabel: 'Últimas 12 horas'},
    {onClick: () => setDate([startOfDay(new Date()), new Date()]), label: 'Hoje', responsiveLabel: 'Hoje'},
  ]

  const [selectedButton, setSelectedButton] = useState<string>(valuesOnRuler[1].label)
  const [selectedButtonResponsive, setSelectedButtonResponsive] = useState<string>('')

  const [hideRuler, setHideRuler] = useState(isRulerHidden || false)
  const windowSize = useWindowSize()

  useEffect(() => {
    if (isRulerHidden) setHideRuler(true)
    else if (windowSize.width <= hideRulerOnSize) setHideRuler(true)
    else setHideRuler(false)
  }, [windowSize])

  const [initialDate, setInitialDate] = useState<Date | null>(new Date())
  const [finalDate, setFinalDate] = useState<Date | null>(new Date())

  const [dateToShow, setDateToShow] = useState<string>()

  const [initialTime, setInitialTime] = useState<string>('00:00')
  const [finalTime, setFinalTime] = useState<string>('23:59')
  const {enqueueSnackbar} = useSnackbar()

  const formatDateToShow = (data: Date) => data.toLocaleDateString('pt-BR')

  const handleInitialDates = () => {
    if (initialDates) {
      setInitialDate(initialDates[0])
      setFinalDate(initialDates[1])

      const initialTime = `${
        initialDates[0]
          .toLocaleDateString('pt-BR', {
            hour: '2-digit',
          })
          .trim()
          .split(',')[1]
      }:${initialDates[0]
        .toLocaleDateString('pt-BR', {
          minute: '2-digit',
        })
        .split(',')[1]
        .trim()}`

      setInitialTime(initialTime.trim())

      const finalTime = `${
        initialDates[1]
          .toLocaleDateString('pt-BR', {
            hour: '2-digit',
          })
          .trim()
          .split(',')[1]
      }:${initialDates[1]
        .toLocaleDateString('pt-BR', {
          minute: '2-digit',
        })
        .split(',')[1]
        .trim()}`

      setFinalTime(finalTime.trim())

      if (disableRangeOnCalendar) {
        setDateToShow(
          initialDates[0].toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          }),
        )
      } else {
        if (isSameDay(initialDates[0], initialDates[1])) {
          setDateToShow(`${initialDates[0].toLocaleString('pt-br', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          })}, ${initialDates[0].toLocaleString('pt-br', {
            hour: '2-digit',
            minute: '2-digit',
          })} até 
        
        ${initialDates[1].toLocaleString('pt-br', {
          hour: '2-digit',
          minute: '2-digit',
        })} 
        `)
        } else {
          setDateToShow(`${formatDateToShow(initialDates[0])} até ${formatDateToShow(initialDates[1])}`)
        }
      }
    }
  }

  useEffect(() => {
    handleInitialDates()
  }, [initialDates])

  const handleInsertDate = () => {
    if (initialDate !== null && finalDate !== null) {
      setLabelTimeSelected?.('')

      if (disableRangeOnCalendar) {
        setDateToShow(
          initialDate.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          }),
        )
      } else {
        setDateToShow(`${formatDateToShow(initialDate)} até ${formatDateToShow(finalDate)}`)
      }

      const [hour, minutes] = initialTime.split(':')
      initialDate.setHours(+hour)
      initialDate.setMinutes(+minutes)
      initialDate.setSeconds(0)

      const [finalHour, finalMinutes] = finalTime.split(':')
      finalDate.setHours(+finalHour)
      finalDate.setMinutes(+finalMinutes)
      finalDate.setSeconds(0)
      if (initialDate.getDate() === finalDate.getDate() && initialDate.getMonth() === finalDate.getMonth() && finalDate.getTime() < initialDate.getTime()) {
        setDateToShow('')
        enqueueSnackbar('O horário final não pode ser maior que o inicial', {variant: 'warning'})
        return
      }

      const sendDatesToFilter: [Date, Date] = [initialDate, finalDate]
      setDate(sendDatesToFilter)
    }
    setAnchor(null)
    setSelectedButton('')
    setSelectedButtonResponsive('')
  }
  const isMobile = windowSize.width <= 1200

  useEffect(() => {
    setLabelTimeSelected?.('30min')
  }, [])

  const resetFiltersOnClose = () => {
    handleInitialDates()
    setAnchor(null)
  }

  const changeInitalDateAndResetHour = (date: Date | null) => {
    if (initialTime !== '00:00') {
      setInitialTime('00:00')
      setFinalTime('23:59')
    }
    setInitialDate(date)
  }

  return (
    <Box display='flex' columnGap='20px'>
      {!hideRuler && (
        <DateBox>
          {valuesOnRuler.map((values) => {
            return (
              <RulerButton
                key={values.label}
                selected={values.label === selectedButton}
                disabled={values.label === selectedButton}
                onClick={() => {
                  setLabelTimeSelected?.(values.label)
                  setSelectedButton(values.label)
                  values.onClick()
                  setDateToShow('')
                  setInitialDate(null)
                  setFinalDate(null)
                  setInitialTime('00:00')
                  setFinalTime('23:59')
                }}>
                {values.label}
              </RulerButton>
            )
          })}
        </DateBox>
      )}

      <Box>
        <CalendarButton onClick={(e) => setAnchor(e.currentTarget)} hideLabel={hideLabel}>
          <ContainerInsideButton>
            <CalendarIcon playerBar={hideLabel} />
            {!hideLabel && (
              <p style={{minWidth: dateToShow ? '200px' : '140px', fontFamily: 'Inter, sans-serif', fontSize: '14px'}}>
                {dateToShow ? dateToShow : 'Personalizar data'}
              </p>
            )}
          </ContainerInsideButton>
        </CalendarButton>
        <Menu hideTriangle anchor={anchor} onClose={() => resetFiltersOnClose()} borderRadius='8px' marginTop={isMobile ? '180px' : '10px'} left='50em'>
          <ModalBox>
            {hideRuler && !isRulerHidden && (
              <>
                <ResponsiveBox>
                  {valuesOnRuler.map((values) => {
                    return (
                      <RulerButton
                        key={values.label}
                        hover
                        selected={values.responsiveLabel === selectedButtonResponsive}
                        onClick={() => {
                          setLabelTimeSelected?.(values.label)
                          setSelectedButtonResponsive(values.responsiveLabel)
                          values.onClick()
                          setDateToShow(values.responsiveLabel)
                          setInitialDate(null)
                          setFinalDate(null)
                          setAnchor(null)
                        }}>
                        {values.responsiveLabel}
                      </RulerButton>
                    )
                  })}
                </ResponsiveBox>

                <Separation width='2px' height='500px' color='#D9D9D9' />
              </>
            )}
            <CalendarBox>
              <Calendar
                max={dateTomorrow}
                setInitalDate={(date) => changeInitalDateAndResetHour(date)}
                setFinalDate={(date) => setFinalDate(date)}
                initialDateValue={initialDate}
                finalDateValue={finalDate}
                disableRange={disableRangeOnCalendar}
              />
              <Box padding='10px 20px 20px' rowGap='5px' display='flex' direction='column'>
                {!hideTime && (
                  <>
                    <Typography variant='p' style={{fontWeight: 'bold'}}>
                      Horário
                    </Typography>
                    <InputBox>
                      <Input.Root
                        label='Início:'
                        height='32px'
                        labelFontSize='12px'
                        fontSize='12px'
                        labelColor='#344054'
                        type='time'
                        controlled
                        value={initialTime}
                        onChange={(e) => setInitialTime(e.target.value)}
                        borderColor='#D0D5DD'
                        leftIcon={<ClockIconCalendar />}
                      />
                      <Input.Root
                        label='Final:'
                        height='32px'
                        labelFontSize='12px'
                        fontSize='12px'
                        labelColor='#344054'
                        type='time'
                        controlled
                        value={finalTime}
                        onChange={(e) => setFinalTime(e.target.value)}
                        borderColor='#D0D5DD'
                        leftIcon={<ClockIconCalendar />}
                      />
                    </InputBox>
                  </>
                )}
                <Box style={{display: 'flex', alignSelf: 'flex-end', marginTop: hideTime ? '0px' : '50px'}}>
                  <Button
                    disabled={!initialDate || !initialTime || !finalDate || !finalTime}
                    disabledTextColor='#B9B9B9'
                    borderRadius='8px'
                    fontWeight={600}
                    fontSize='12px'
                    height='32px'
                    padding='0px 30px'
                    onClick={() => handleInsertDate()}>
                    Aplicar
                  </Button>
                </Box>
              </Box>
            </CalendarBox>
          </ModalBox>
        </Menu>
      </Box>
    </Box>
  )
}

export default CustomCalendar
